/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AuthenticationInfoRes,
  EnabledPolicyTypeEnum,
  IdSyncStatus,
  ProvisionModalState,
  ProvisionStatusEnum,
  RejectProvisionReason,
  TokenStatus,
} from '../../../types/provision';
import { ProvisionApi } from '@Apis';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import {
  DataSyncFailed,
  DataSynced,
  DataSyncing,
  ProvisionOrgInfo,
  StepIcon,
} from './share';
import { reportPendo } from '@Utils/setupPendo';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import { StepProps, Steps } from 'antd';
import RejectProvisionModal from './rejectProvisionModal';
import { GlobalCustomEvent } from '@Utils/enums';
import { Service } from '@Types/app';
import { useProvisionEntityInfo } from '@Models/useProvisionEntityInfo';

enum CommonStatus {
  NotProvision = 0,
  Provisioning = 1,
  Provisioned = 2,
}
export type CommonProvisionModalState = ProvisionModalState & {
  service: Service.Mip | Service.IdentityProtection;
};

/**
 * @description modal for mip and IdentityProtection
 */
const MipModal = ({
  service,
  orgId,
  onClose,
  isOpen,
  provisionStatus,
}: CommonProvisionModalState) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['provision', 'common']);
  const StatusMap = {
    [ProvisionStatusEnum.NotStarted]: CommonStatus.NotProvision,
    [ProvisionStatusEnum.Provisioning]: CommonStatus.Provisioning,
    [ProvisionStatusEnum.Provisioned]: CommonStatus.Provisioned,
  };

  const [syncDataFailFlag, setSyncDataFailFlag] = useState(
    provisionStatus.status === ProvisionStatusEnum.Failure &&
      [IdSyncStatus.Failure, 'FAILURE'].includes(
        provisionStatus?.idSync?.status
      )
  );
  const [commonStatus, setCommonStatus] = useState<CommonStatus>(
    // StatusMap[provisionStatus.status] || SharePointStatus.NotProvision
    () => {
      if (Object.keys(StatusMap).includes(provisionStatus.status)) {
        return StatusMap[provisionStatus.status as keyof typeof StatusMap];
      } else {
        //provisionStatus.status = failure
        //token failure
        if (provisionStatus.authorization.status === TokenStatus.Failure) {
          return CommonStatus.NotProvision;
        } else {
          //id sync fail
          return CommonStatus.Provisioning;
          //TODO: 同时设置 id sync 为失败的情况
        }
      }
    }
  );
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | AuthenticationInfoRes>(null);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const titleTextStyle = {
    fontSize: '16px',
    lineHeight: '22px',
    fontWeight: '400',
    color: colorStyle.color.primary,
  };

  const stepItems: StepProps[] = [
    {
      icon: <StepIcon checked={commonStatus >= CommonStatus.Provisioning} />,
      title: (
        <Text pt="4px" {...titleTextStyle}>
          {t(`provision.service.${service}.grantPermission`)}
        </Text>
      ),
      description:
        commonStatus === CommonStatus.NotProvision ? (
          <GrantPermission
            service={service}
            authenticationInfo={authenticationInfo}
            setCommonStatus={setCommonStatus}
            setErrorMsg={setErrorMsg}
            orgId={orgId}
          />
        ) : (
          <Text color={colorStyle.color.tertiary} pt="8px" lineHeight="20px">
            {t('provision.common.permissionGranted')}
          </Text>
        ),
    },
  ];

  const [rejectReason, setRejectReason] =
    useState<RejectProvisionReason | null>(null);
  useEffect(() => {
    if (commonStatus === CommonStatus.NotProvision) {
      ProvisionApi.apiGetAuthenticationInfo(service, orgId)
        .then((res) => {
          setAuthenticationInfo(res.data);
        })
        .catch((e) => {
          console.error(JSON.stringify(e));
          const errorCode = e.data?.error?.details?.[0].errorCode;
          if (
            errorCode === RejectProvisionReason.ServiceConflict ||
            errorCode === RejectProvisionReason.PocConflict
          ) {
            setRejectReason(errorCode);
          }
        });
    }
  }, []);

  if (rejectReason) {
    return (
      <RejectProvisionModal
        onClose={onClose}
        service={service}
        reason={rejectReason}
      />
    );
  }

  if (commonStatus === CommonStatus.NotProvision && !authenticationInfo) {
    return <></>;
  }

  return (
    <>
      <Modal isOpen={isOpen} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          {/* TODO: i18n: "Grant Access to Exchange Online" */}
          <ModalHeader>{t(`provision.service.${service}.title`)} </ModalHeader>
          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <Flex direction="column" gap="16px">
              <Steps
                direction="vertical"
                current={commonStatus}
                items={stepItems}
              />
              {commonStatus === CommonStatus.Provisioning &&
                (syncDataFailFlag ? (
                  <DataSyncFailed
                    service={service}
                    onRetry={async () => {
                      await ProvisionApi.apiRetrySyncData(service, orgId);
                      setCommonStatus(CommonStatus.Provisioning);
                      setSyncDataFailFlag(false);
                    }}
                  />
                ) : (
                  <DataSyncing
                    onRefresh={async () => {
                      const res = await ProvisionApi.apiGetProvisionStatus(
                        service,
                        orgId
                      );
                      if (res.data.status === ProvisionStatusEnum.Provisioned) {
                        if (service === Service.Mip) {
                          window.dispatchEvent(
                            new CustomEvent(
                              GlobalCustomEvent.FreshGlobalSetting
                            )
                          );
                        }
                        setCommonStatus(CommonStatus.Provisioned);
                      }
                    }}
                  />
                ))}
              {commonStatus === CommonStatus.Provisioned && <DataSynced />}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default MipModal;

const GrantPermission = ({
  service,
  authenticationInfo,
  setCommonStatus,
  setErrorMsg,
  orgId,
}: {
  service: Service.Mip | Service.IdentityProtection;
  authenticationInfo: AuthenticationInfoRes | null;
  setCommonStatus: (status: CommonStatus) => void;
  setErrorMsg: (msg: string | undefined) => void;
  orgId: string;
}) => {
  const { t } = useTranslation(['provision', 'common']);
  const [loading, toggleLoading] = useToggle(false);
  const { getPrimaryApp } = useProvisionEntityInfo();

  const cookieName = {
    [Service.Mip]: 'provision_status_mip_check_license',
    [Service.IdentityProtection]:
      'provision_status_microsoft_identity_protection_api',
  }[service];

  const timeRef = useRef<any>(null);
  const onClick = async () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service,
    });
    setErrorMsg(undefined);
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const provisionInfo = getProvisionCookie(cookieName);
      if (provisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(provisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              getPrimaryApp(service),
              code,
              EnabledPolicyTypeEnum.None,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.${service}.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setCommonStatus(CommonStatus.Provisioning);
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t(`provision.service.${service}.error.deniedGrant`));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Box pt="8px">
        <Button variant="secondary" borderRadius="2rem" onClick={onClick}>
          {t('provision.button.grantPermission')}
        </Button>
      </Box>
    </>
  );
};
